import { useState } from "react";
import { toast } from "sonner";
import PageLayout from "../components/layout/PageLayout";
import api, { formatApiError } from "../lib/api";
import { Mail, Phone, MapPin, Send } from "lucide-react";

const INQUIRY_TYPES = [
    "Athlete representation",
    "Parent consultation",
    "Sponsor partnership",
    "Brand campaign",
    "Media request",
    "Speaking engagement",
    "Camps and clinics",
    "School presentation",
    "General support",
];

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", inquiry_type: "General support", subject: "", message: "" });
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);

    const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) { toast.error("Name, email and message are required."); return; }
        setSending(true);
        try {
            await api.post("/inquiries", form);
            setDone(true);
            toast.success("Message received. We'll be in touch.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    };

    return (
        <PageLayout>
            <section className="pt-32 pb-16 border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="section-label mb-5">Contact & Booking</div>
                    <h1 className="font-display text-5xl md:text-7xl text-white leading-none tracking-tight" data-testid="contact-heading">
                        LET'S <span className="text-[#d4af37]">TALK.</span>
                    </h1>
                </div>
            </section>

            <section className="py-16">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4 space-y-5">
                        {[
                            { icon: Mail, t: "Email", v: "hello@digitalbeast.com" },
                            { icon: Phone, t: "Phone", v: "Available upon inquiry" },
                            { icon: MapPin, t: "Headquarters", v: "Lincoln, Nebraska · Nationwide" },
                        ].map(({icon: I, t, v}) => (
                            <div key={t} className="surface p-6 flex items-start gap-4" data-testid={`contact-info-${t.toLowerCase()}`}>
                                <I className="text-[#d4af37]" size={20} strokeWidth={1.5} />
                                <div>
                                    <div className="text-white/40 text-xs uppercase tracking-[0.24em]">{t}</div>
                                    <div className="text-white text-base mt-1">{v}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="lg:col-span-8">
                        {done ? (
                            <div className="surface p-14 text-center" data-testid="contact-success">
                                <div className="font-display text-4xl text-[#d4af37]">MESSAGE SENT.</div>
                                <p className="text-white/60 mt-4">A member of the Digital Beast team will get back to you within 2 business days.</p>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="surface p-8 space-y-5" data-testid="contact-form">
                                <div className="grid md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="db-label">Full Name *</label>
                                        <input className="db-input-block" value={form.name} onChange={e => setF("name", e.target.value)} data-testid="contact-name" />
                                    </div>
                                    <div>
                                        <label className="db-label">Email *</label>
                                        <input type="email" className="db-input-block" value={form.email} onChange={e => setF("email", e.target.value)} data-testid="contact-email" />
                                    </div>
                                    <div>
                                        <label className="db-label">Phone</label>
                                        <input className="db-input-block" value={form.phone} onChange={e => setF("phone", e.target.value)} data-testid="contact-phone" />
                                    </div>
                                    <div>
                                        <label className="db-label">Inquiry Type</label>
                                        <select className="db-input-block" value={form.inquiry_type} onChange={e => setF("inquiry_type", e.target.value)} data-testid="contact-type">
                                            {INQUIRY_TYPES.map(x => <option key={x}>{x}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="db-label">Subject</label>
                                    <input className="db-input-block" value={form.subject} onChange={e => setF("subject", e.target.value)} data-testid="contact-subject" />
                                </div>
                                <div>
                                    <label className="db-label">Message *</label>
                                    <textarea className="db-input-block db-textarea" value={form.message} onChange={e => setF("message", e.target.value)} data-testid="contact-message" />
                                </div>
                                <button type="submit" disabled={sending} className="btn-gold" data-testid="contact-submit">
                                    {sending ? "Sending…" : "Send Message"} <Send size={14} />
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </PageLayout>
    );
}
